import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Page from 'flarum/common/components/Page';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostStreamState from 'flarum/forum/states/PostStreamState';
import BlogPostController from '../components/BlogPostController';
import BlogItemSidebar from '../components/BlogItemSidebar/BlogItemSidebar';
import Link from 'flarum/common/components/Link';
import BlogOverview from './BlogOverview';
import fullTime from 'flarum/common/helpers/fullTime';
import ArticleSubscription from '../components/ArticleSubscription';
import classList from 'flarum/common/utils/classList';
import ItemList from 'flarum/common/utils/ItemList';
import Icon from 'flarum/common/components/Icon';
import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';

// The discussion is fetched via the store / preloaded document, so the response
// model carries the raw JSON:API `payload` alongside the regular model getters.

export default class BlogItem extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "near", void 0);
    _defineProperty(this, "loading", void 0);
    _defineProperty(this, "found", void 0);
    _defineProperty(this, "article", void 0);
    _defineProperty(this, "stream", void 0);
    /**
     * Core's `PostStream` lives in a lazy chunk, so it is imported on demand
     * rather than statically.
     */
    _defineProperty(this, "PostStream", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(extractText(app.translator.trans('fof-blog.forum.blog')));

    // Send history push
    app.history.push('blogArticle', extractText(app.translator.trans('fof-blog.forum.blog')));
    this.bodyClass = 'BlogItemPage';
    this.near = Number(m.route.param('near')) || 0;
    this.loading = true;
    this.found = false;
    this.article = null;
    flarum.reg.asyncModuleImport('flarum/forum/components/PostStream').then(_ref => {
      let PostStream = _ref.default;
      this.PostStream = PostStream;
      m.redraw();
    });
    this.loadBlogItem();
  }

  // Load blog overview
  loadBlogItem() {
    const preloadBlogOverview = app.preloadedApiDocument();
    if (preloadBlogOverview) {
      // We must wrap this in a setTimeout because if we are mounting this
      // component for the first time on page load, then any calls to m.redraw
      // will be ineffective and thus any configs (scroll code) will be run
      // before stuff is drawn to the page.
      setTimeout(this.show.bind(this, preloadBlogOverview), 0);
    } else {
      app.store.find('discussions', m.route.param('id').split('-')[0]).then(this.show.bind(this)).catch(() => {
        m.redraw();
      });
    }
    m.redraw();
  }

  // Show blog post
  show(article) {
    // Set article data
    this.article = article;

    // Update title
    app.setTitle("".concat(article.title(), " - ").concat(extractText(app.translator.trans('fof-blog.forum.blog'))));
    this.loading = false;
    let includedPosts = [];
    if (article.payload && article.payload.included) {
      const articleId = article.id();
      includedPosts = article.payload.included.filter(record => record.type === 'posts' && !!record.relationships && !!record.relationships.discussion && record.relationships.discussion.data.id === articleId).map(record => app.store.getById('posts', record.id)).sort((a, b) => Number(a.id()) - Number(b.id())).slice(0, 20);
    }
    this.stream = new PostStreamState(article, includedPosts);

    // Scroll to specific post
    if (this.near) {
      this.stream.goToNumber(this.near || 0, true).then(() => {
        app.current.set('discussion', article);
        app.current.set('stream', this.stream);
      });
    }

    // Read post on load
    if (app.session.user && !article.lastReadPostNumber()) {
      article.save({
        lastReadPostNumber: 1
      });
    }
    m.redraw();
  }
  postItems() {
    var _this$article2, _this$article2$title, _this$article3, _this$article3$isHidd;
    let articlePost = null;
    if (!this.loading && this.article) {
      var _this$article, _this$article$firstPo;
      articlePost = (_this$article = this.article) == null || (_this$article$firstPo = _this$article.firstPost) == null ? void 0 : _this$article$firstPo.call(_this$article);
    }
    const items = new ItemList();
    items.add('title', m("h1", {
      className: classList('FoFBlog-Article-Title', {
        'FoFBlog-Article-GhostTitle': this.loading
      })
    }, (this == null || (_this$article2 = this.article) == null || (_this$article2$title = _this$article2.title) == null ? void 0 : _this$article2$title.call(_this$article2)) || 'Ghost title', ((_this$article3 = this.article) == null || (_this$article3$isHidd = _this$article3.isHidden) == null ? void 0 : _this$article3$isHidd.call(_this$article3)) && "(".concat(app.translator.trans('fof-blog.forum.hidden'), ")")), 100);
    items.add('publishDate', m("div", {
      className: classList('FoFBlog-Article-PublishDate', {
        'FoFBlog-Article-GhostPublishDate': this.loading
      })
    }, this.article ? fullTime(this.article.createdAt()) : m("span", null, "\xA0")), 80);
    if (this.loading) {
      items.add('skeleton', [0, 1, 2].map(() => m("div", null, m("p", {
        className: 'FoFBlog-Article-GhostParagraph'
      }, "\xA0"), m("p", {
        className: 'FoFBlog-Article-GhostParagraph'
      }, "\xA0"), m("p", {
        className: 'FoFBlog-Article-GhostParagraph'
      }, "\xA0"), m("p", null, "\xA0"))), 60);
    }
    if (!this.loading && this.article) {
      const meta = this.article.blogMeta();
      if (meta && meta.isPendingReview()) {
        items.add('review', m("div", {
          className: 'Post-body'
        }, m("blockquote", {
          class: "uncited",
          style: {
            fontSize: '16px'
          }
        }, m("div", null, m(Icon, {
          name: "far fa-clock",
          style: {
            marginRight: '5px'
          }
        }), " ", app.translator.trans('fof-blog.forum.review_article.pending_review')))), 60);
      }
      if (articlePost) {
        items.add('post', m(CommentPost, {
          post: articlePost
        }), 40);
      }
    }
    return items;
  }
  contentItems() {
    var _this$article4, _this$article4$isHidd, _app$session$user, _app$session$user$can, _this$article5, _this$article5$canRen, _, _$canEdit, _this$article6, _this$article6$posts, _this$article7, _this$article7$tags;
    const defaultImage = app.forum.attribute('blogDefaultImageUrl') ? "url(".concat(app.forum.attribute('blogDefaultImageUrl'), ")") : null;
    const meta = this.article ? this.article.blogMeta() : false;
    const blogImage = meta && meta.featuredImage() ? "url(".concat(meta.featuredImage(), ")") : defaultImage;
    const items = new ItemList();
    items.add('image', m("div", {
      className: classList('FoFBlog-Article-Image FoFBlog-default-image', {
        'FoFBlog-Article-GhostImage': this.loading
      }),
      style: {
        backgroundImage: blogImage,
        opacity: (_this$article4 = this.article) != null && (_this$article4$isHidd = _this$article4.isHidden) != null && _this$article4$isHidd.call(_this$article4) ? 0.4 : null
      }
    }), 100);
    if (this.article && ((_app$session$user = app.session.user) != null && (_app$session$user$can = _app$session$user.canEdit) != null && _app$session$user$can.call(_app$session$user) || (_this$article5 = this.article) != null && (_this$article5$canRen = _this$article5.canRename) != null && _this$article5$canRen.call(_this$article5) || (_ = (((_this$article6 = this.article) == null || (_this$article6$posts = _this$article6.posts) == null ? void 0 : _this$article6$posts.call(_this$article6)) || [])[0]) != null && (_$canEdit = _.canEdit) != null && _$canEdit.call(_))) {
      items.add('editArticle', m(BlogPostController, {
        article: this.article
      }), 80);
    }

    // Article Categories
    items.add('categories', m("div", {
      className: "FoFBlog-Article-Categories"
    }, !this.loading && (((_this$article7 = this.article) == null || (_this$article7$tags = _this$article7.tags) == null ? void 0 : _this$article7$tags.call(_this$article7)) || []).map(tag => m(Link, {
      href: app.route('blogCategory', {
        slug: tag.slug()
      })
    }, tag.name())), this.loading && [0, 1].map(() => m("span", {
      className: "FoFBlog-Article-GhostCategory"
    }, "Category"))), 60);
    items.add('post', m("div", {
      className: 'FoFBlog-Article-Post'
    }, this.postItems().toArray()), 40);
    return items;
  }
  articleItems() {
    var _this$article8, _this$article8$isLock, _this$article9, _this$article9$commen;
    const items = new ItemList();
    items.add('content', m("div", {
      className: "FoFBlog-Article-Content"
    }, this.contentItems().toArray()), 100);
    if (!(this != null && (_this$article8 = this.article) != null && (_this$article8$isLock = _this$article8.isLocked) != null && _this$article8$isLock.call(_this$article8) && (this == null || (_this$article9 = this.article) == null || (_this$article9$commen = _this$article9.commentCount) == null ? void 0 : _this$article9$commen.call(_this$article9)) === 1)) {
      var _this$article0, _this$article0$isLock;
      items.add('comments', m("div", {
        className: 'FoFBlog-Article-Comments'
      }, !this.loading && app.session.user && this.article && this.article.subscription && (!this.article.isLocked || this.article.isLocked && !this.article.isLocked()) && m(ArticleSubscription, {
        discussion: this.article
      }), m("h4", null, app.translator.trans('fof-blog.forum.comment_section.comments'), " (", this.article ? this.article.commentCount() - 1 : 0, ")"), !this.loading && ((_this$article0 = this.article) == null || (_this$article0$isLock = _this$article0.isLocked) == null ? void 0 : _this$article0$isLock.call(_this$article0)) && m("div", {
        className: 'Post-body'
      }, m("blockquote", {
        class: "uncited"
      }, m("div", null, m("span", {
        className: 'far fa-lock',
        style: {
          marginRight: '5px'
        }
      }), " ", app.translator.trans('fof-blog.forum.comment_section.locked')))), !this.loading && this.article && this.PostStream && m(this.PostStream, {
        discussion: this.article,
        stream: this.stream,
        onPositionChange: this.positionChanged.bind(this)
      })), 75);
    }
    return items;
  }

  /**
   * The blog reuses the forum's welcome hero, when enabled.
   */
  hero() {
    if (!app.forum.attribute('blogAddHero')) return null;
    return m(WelcomeHero, null);
  }
  view() {
    return [this.hero(), m("div", {
      className: 'FoFBlogItem'
    }, m("div", {
      className: 'container'
    }, m("div", {
      className: 'FoFBlog-ToolButtons'
    }, m(Link, {
      href: app.route('blog'),
      className: 'Button',
      onclick: e => {
        if (app.previous.matches(BlogOverview)) {
          e.preventDefault();
          history.back();
        }
      }
    }, m("i", {
      class: "icon fas fa-angle-left Button-icon"
    }), m("span", {
      class: "Button-label"
    }, app.translator.trans('fof-blog.forum.return_to_overview')))), m("div", {
      className: 'FoFBlog-Article'
    }, m("div", {
      className: 'FoFBlog-Article-Container'
    }, this.articleItems().toArray()), m(BlogItemSidebar, {
      article: this.article,
      loading: this.loading
    }))))];
  }
  positionChanged(startNumber, endNumber) {
    const article = this.article;
    if (article && app.session.user && endNumber > (article.lastReadPostNumber() || 0)) {
      article.save({
        lastReadPostNumber: endNumber
      });
      m.redraw();
    }
  }
}
flarum.reg.add('fof-blog', 'forum/pages/BlogItem', BlogItem);